import React, { useEffect, Suspense, useCallback, useMemo, useRef } from "react";
import { Virtuoso } from "react-virtuoso";
import { useObjectStore } from "../zustand/objects";
import { useShallow } from "zustand/react/shallow";
import LayerListItem from "./LayerListItem";
import {
  IconLock,
  IconTypography,
  IconPhoto,
  IconPalette,
  IconSparkles,
  IconCamera,
} from '@tabler/icons-react';
// MANTINE
import { Accordion, Badge, Text, Group } from '@mantine/core';
import { getEditorChromeTheme } from "../utils/editorChromeTheme";

/** Image-style categories from the left nav “Add image” menu (FloorColourButton). */
const LAYER_IMAGE_MENU_CATEGORIES = new Set([
  "image",
  "apple",
  "microsoft",
  "floatgridsUi",
  "floatgridsLayout",
  "background",
]);

const CubeIcon = ({ borderColour }) => {
  const strokeWidth = 35;

  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 512 512" fill="none">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M245.637 59.0793C252.078 55.5054 259.908 55.5076 266.347 59.0852L447.681 159.829C454.453 163.592 458.654 170.731 458.654 178.478V333.521C458.654 341.269 454.453 348.407 447.681 352.17L266.354 452.91C259.911 456.49 252.076 456.49 245.633 452.911L64.2935 352.17C57.5208 348.407 53.3203 341.269 53.3203 333.521V178.341C53.3203 170.59 57.5253 163.448 64.3037 159.687L245.637 59.0793Z"
        stroke={borderColour}
        strokeWidth={strokeWidth}
      />
      <path d="M255.992 278.511V458.677" stroke={borderColour} strokeWidth={strokeWidth} />
      <path d="M255.985 278.507L53.3203 165.917" stroke={borderColour} strokeWidth={strokeWidth} />
      <path d="M458.661 165.917L255.992 278.507" stroke={borderColour} strokeWidth={strokeWidth} />
    </svg>
  );
};

const LayerRowIcon = ({ objects, selected, color }) => {
  const iconColor = selected ? "#fe9a00" : color;
  const stroke = 1.5;
  const size = 22;
  const { id, category, object: objectKind } = objects;

  if (id === "camera" || objectKind === "camera") {
    return <IconCamera size={size} color={iconColor} stroke={stroke} />;
  }
  if (category === "text") {
    return <IconTypography size={size} color={iconColor} stroke={stroke} />;
  }
  if (category === "figma") {
    return <IconPalette size={size} color={iconColor} stroke={stroke} />;
  }
  if (category === "ai" || category === "aiModel") {
    return <IconSparkles size={size} color={iconColor} stroke={stroke} />;
  }
  if (category && LAYER_IMAGE_MENU_CATEGORIES.has(category)) {
    return <IconPhoto size={size} color={iconColor} stroke={stroke} />;
  }

  return <CubeIcon borderColour={iconColor} />;
};


const LayerList = () => {
  const primaryColor = "#FE9A00";
  const {
    objectsAreLoaded,
    artboards,
    currentObjectArtboard,
    addToGroupedObjects,
    removeFromGroupedObjects,
    grouping,
    selectedObjectID,
    updateObjectSelected,
    groupedObjects,
    arrangeUndoRedo,
    textModalOpen,
    removeObject,
    editingObjectTitle,
    viewportThemeOverride,
  } = useObjectStore(
    useShallow((s) => ({
      objectsAreLoaded: s.objectsAreLoaded,
      artboards: s.artboards,
      currentObjectArtboard: s.currentObjectArtboard,
      addToGroupedObjects: s.addToGroupedObjects,
      removeFromGroupedObjects: s.removeFromGroupedObjects,
      grouping: s.grouping,
      selectedObjectID: s.selectedObjectID,
      updateObjectSelected: s.updateObjectSelected,
      groupedObjects: s.groupedObjects,
      arrangeUndoRedo: s.arrangeUndoRedo,
      textModalOpen: s.textModalOpen,
      removeObject: s.removeObject,
      editingObjectTitle: s.editingObjectTitle,
      viewportThemeOverride: s.viewportThemeOverride,
    }))
  );
  const isDark = viewportThemeOverride === "dark";
  const chrome = getEditorChromeTheme(isDark);
  const layerTheme = isDark
    ? {
        heading: chrome.title,
        headingBadgeBg: chrome.bg,
        headingBadgeText: chrome.title,
        itemBg: chrome.bg,
        itemBorder: chrome.border,
        itemSelectedBg: "#3D3428",
        itemSelectedBorder: primaryColor,
        itemText: chrome.body,
        itemIcon: chrome.muted,
        chevron: chrome.iconHover,
        controlBorder: chrome.border,
        panelText: chrome.title,
        controlIcon: chrome.iconHover,
        panelPlaceholder: chrome.muted,
      }
    : {
        heading: chrome.title,
        headingBadgeBg: "#F5F5F4",
        headingBadgeText: chrome.body,
        itemBg: "#FAFAF9",
        itemBorder: chrome.border,
        itemSelectedBg: "#FFFBEB",
        itemSelectedBorder: primaryColor,
        itemText: chrome.body,
        itemIcon: chrome.muted,
        chevron: chrome.iconHover,
        controlBorder: chrome.title,
        panelText: chrome.title,
        controlIcon: chrome.iconHover,
        panelPlaceholder: chrome.muted,
      };

  const virtuosoRef = useRef(null);

  const layers = useMemo(() => {
    if (!artboards || !currentObjectArtboard) return [];
    return artboards[currentObjectArtboard] || [];
  }, [artboards, currentObjectArtboard]);

const checkboxClick = (e) => {
  e.currentTarget.checked ? addToGroupedObjects(e.target.value) : removeFromGroupedObjects(e.target.value) 
}

  useEffect(() => {
    if (!objectsAreLoaded) return;
    const st = useObjectStore.getState();
    const list = st.artboards?.[st.currentObjectArtboard];
    if (!list?.length) return;
    if (selectedObjectID !== "0" && selectedObjectID != null) {
      const idx = list.findIndex((o) => o.id === selectedObjectID);
      if (idx >= 0) {
        virtuosoRef.current?.scrollToIndex({ index: idx, align: "center", behavior: "smooth" });
      }
    } else {
      virtuosoRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [objectsAreLoaded, selectedObjectID, currentObjectArtboard]);

const closeListItem = useCallback(() => {
  updateObjectSelected('0');
}, [updateObjectSelected]);

  // KEYBOARD OBJECT FUNCTIONS
  const handleKeyDown = useCallback((event) => {
    if (event.repeat) { return }
    if (event.key === 'Backspace') { 
      if (textModalOpen === false) {
        if (!editingObjectTitle) {
          // send current state to undoActions
          arrangeUndoRedo({
            updateType: "objectRemove",
            artboard: currentObjectArtboard,
            objectID: selectedObjectID,
            undo: false,
            data: artboards[currentObjectArtboard].find((x) => x.id === selectedObjectID)
          })
          // send update to zustand
        removeObject({ currentObjectArtboard: currentObjectArtboard, id:selectedObjectID, undo: false })
        updateObjectSelected('0')}}
    } 
    else if (event.key === 'Escape') {
      closeListItem()
    }
    else if (event.key === 'Tab') { 
      
      const index = artboards[currentObjectArtboard].findIndex(obj => obj.id === selectedObjectID);
      const arrayLength = artboards[currentObjectArtboard].length
      let nextIndex;
      if (index === arrayLength - 1) { // Reached the end, cycle back to the beginning
        nextIndex = 0;
      } else {
        nextIndex = index + 1; // Move to the next object
      }  
      (textModalOpen === false) && updateObjectSelected(artboards[currentObjectArtboard][nextIndex].id);
    }
  }, [
    arrangeUndoRedo,
    artboards,
    closeListItem,
    currentObjectArtboard,
    editingObjectTitle,
    removeObject,
    selectedObjectID,
    textModalOpen,
    updateObjectSelected
  ]);

  // KEYBOARD OBJECT FUNCTIONS
  const handleKeyDownNotSelected = useCallback((event) => {
    if (event.repeat) { return }
    if (event.key === 'Tab') {    
      (textModalOpen === false) && updateObjectSelected(artboards[currentObjectArtboard][0].id);
    }
  }, [artboards, currentObjectArtboard, textModalOpen, updateObjectSelected]); 

  useEffect(() => {
      if (!artboards || !currentObjectArtboard) return;
    if (artboards[currentObjectArtboard].length > 0) {
      if (selectedObjectID === '0') {
        document.addEventListener('keydown', handleKeyDownNotSelected)
      }
      else {
        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown);
      } 
      return () => document.removeEventListener('keydown', handleKeyDownNotSelected)
    }
       
  },[
    artboards,
    currentObjectArtboard,
    handleKeyDown,
    handleKeyDownNotSelected,
    objectsAreLoaded,
    selectedObjectID,
    textModalOpen
  ])

  if (!objectsAreLoaded) {
    return <div></div>;
  } else {
    return (
      <div
        id="table"
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
          paddingBottom: 0,
          paddingLeft: 0,
          paddingRight: 0,
          margin: 0,
        }}
      >
        <Group justify="space-between" align="center" wrap="nowrap" mb={20}>
          <Text
            fw={600}
            size={11}
            mb={0}
            tt="uppercase"
            style={{
              fontFamily: "Inter, sans-serif",
              color: layerTheme.heading,
              letterSpacing: "0.55px",
              lineHeight: "16.5px",
            }}
          >
            Scene elements
          </Text>
          <Badge
            size="sm"
            mb={0}
            variant="light"
            color="gray"
            styles={{
              root: {
                backgroundColor: layerTheme.headingBadgeBg,
                color: layerTheme.headingBadgeText,
                fontWeight: 500,
                fontSize: 10,
                lineHeight: "15px",
                height: 19,
                minHeight: 19,
                paddingLeft: 8,
                paddingRight: 8,
                borderRadius: 4,
                border: "none",
              },
            }}
          >
            {layers.length}
          </Badge>
        </Group>

        <Accordion
          variant="separated"
          radius={4}
          value={selectedObjectID}
          onChange={(e) => updateObjectSelected(e)}
          pb={0}
          style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
          styles={{ root: { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" } }}
        >
          <Virtuoso
            ref={virtuosoRef}
            className="mbx-hide-scrollbar"
            style={{ flex: 1, minHeight: 0, scrollbarWidth: "none", msOverflowStyle: "none" }}
            data={layers}
            defaultItemHeight={76}
            increaseViewportBy={{ top: 160, bottom: 280 }}
            computeItemKey={(_, obj) => obj.id}
            itemContent={(_index, objects) => (
              <Suspense fallback={null}>
                <Accordion.Item
                  value={objects.id}
                  mb={8}
                  style={{
                    borderWidth: 2,
                    borderColor:
                      selectedObjectID === objects.id
                        ? layerTheme.itemSelectedBorder
                        : layerTheme.itemBorder,
                    backgroundColor:
                      selectedObjectID === objects.id
                        ? layerTheme.itemSelectedBg
                        : layerTheme.itemBg,
                    borderRadius: 4,
                    boxShadow:
                      selectedObjectID === objects.id
                        ? "0px 1px 3px 0px rgba(0, 0, 0, 0.10), 0px 1px 2px 0px rgba(0, 0, 0, 0.10)"
                        : undefined,
                  }}
                >
                  <Accordion.Control
                    icon={objects.isLocked && <IconLock size={18} color={layerTheme.itemIcon} />}
                    styles={{
                      chevron: {
                        color:
                          selectedObjectID === objects.id
                            ? layerTheme.chevron
                            : layerTheme.itemIcon,
                      },
                    }}
                  >
                    <Group gap={8}>
                      <LayerRowIcon
                        objects={objects}
                        selected={selectedObjectID === objects.id}
                        color={layerTheme.itemIcon}
                      />
                      <Text
                        size="sm"
                        fw={400}
                        style={{
                          color: layerTheme.itemText,
                          lineHeight: "20px",
                          letterSpacing: "-0.15px",
                        }}
                      >
                        {objects.objectTitle}
                      </Text>
                    </Group>
                  </Accordion.Control>
                  <div id={objects.id} style={{ display: "flex" }}>
                    {grouping && objects.id !== "camera" && (
                      <input
                        value={objects.id}
                        type="checkbox"
                        style={{ cursor: "pointer", marginRight: 5 }}
                        onChange={(e) => checkboxClick(e)}
                        checked={groupedObjects.includes(objects.id)}
                      />
                    )}
                    <Accordion.Panel>
                      {objects.id !== "camera" && selectedObjectID === objects.id && (
                        <LayerListItem
                          key={objects.id}
                          id={objects.id}
                          position={objects.position}
                          rotation={objects.rotation}
                          destination={objects.destination}
                          scale={objects.scale}
                          category={objects.category}
                          object={objects.object}
                          transformMode={objects.transformMode}
                          content={objects.content}
                          textKind={objects.textKind}
                          colour={objects.colour}
                          bGColour={objects.bGColour}
                          pose={objects.pose}
                          title={objects.objectTitle}
                          texture={objects.imageTexture}
                          isLocked={objects.isLocked}
                          matrix={objects.matrixState}
                          figmaID={objects.figmaID}
                          figmaURL={objects.figmaURL}
                          figmaSize={objects.figmaSize}
                          image={objects.image}
                          imageSize={objects.imageSize}
                          curved={objects.curved}
                          curveAmount={objects.curveAmount}
                          uploadedURL={objects.uploadedURL}
                          objectNotes={objects.objectNotes}
                          uiTheme={{
                            controlBorder: layerTheme.controlBorder,
                            controlIcon: layerTheme.controlIcon,
                            panelText: layerTheme.panelText,
                            panelPlaceholder: layerTheme.panelPlaceholder,
                          }}
                        />
                      )}
                    </Accordion.Panel>
                  </div>
                </Accordion.Item>
              </Suspense>
            )}
          />
        </Accordion>
      </div>
    );
  }
};

export default LayerList;
